import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { hasClaimedDailyCode, claimDailyCodeReward } from '../services/walletService';

const DailyCodeRedemption = ({ onRewardClaimed }) => {
  const { currentUser } = useAuth();
  const { refreshWallet } = useWallet();
  const [dailyCode, setDailyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasClaimed, setHasClaimed] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  const [showInput, setShowInput] = useState(true);

  useEffect(() => {
    // Check if user has already claimed the daily code
    const checkClaimed = async () => {
      if (!currentUser) return;
      
      try {
        const claimed = await hasClaimedDailyCode(currentUser.uid);
        setHasClaimed(claimed);
        setShowInput(!claimed);
        
        if (claimed) {
          setMessage("You've already redeemed today's code!");
          setMessageType('info');
        }
      } catch (error) {
        console.error("Error checking daily code claim status:", error);
      }
    };
    
    checkClaimed();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !dailyCode.trim()) return;
    
    setLoading(true);
    setMessage('');
    
    try {
      // Check if user has already claimed today before proceeding
      const alreadyClaimed = await hasClaimedDailyCode(currentUser.uid);
      if (alreadyClaimed) {
        setMessageType('error');
        setMessage("You've already claimed today's reward. Come back tomorrow!");
        setHasClaimed(true);
        setShowInput(false);
        setLoading(false);
        return;
      }
      
      // Attempt to claim the daily code reward
      const result = await claimDailyCodeReward(currentUser.uid, dailyCode.trim());
      
      if (result.success) {
        setMessageType('success');
        setMessage(`${result.message} You received ${result.amount.toLocaleString()} coins!`);
        setHasClaimed(true);
        setShowInput(false);
        
        // Refresh wallet to show updated balance
        await refreshWallet();
        
        // Call the callback if provided
        if (onRewardClaimed) {
          onRewardClaimed();
        }
      } else {
        setMessageType('error');
        setMessage(result.message);
      }
    } catch (error) {
      console.error("Error claiming daily code reward:", error);
      setMessageType('error');
      setMessage("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getMessageStyles = () => {
    switch (messageType) {
      case 'success':
        return 'bg-green-100 text-green-800 dark:bg-green-200 dark:text-green-900';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-200 dark:text-red-900';
      case 'info':
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-200 dark:text-blue-900';
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg shadow-sm p-5 border border-purple-100 dark:border-purple-800/30">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
        <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
        </svg>
        Daily Code Reward
      </h2>
      
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm border border-purple-100 dark:border-purple-900/30">
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">How it works:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Enter the daily code provided by the admin</li>
            <li>Claim 1,000 coins instantly when the code matches</li>
            <li>Limited to one redemption per day</li>
            <li>
              <a 
                href="https://t.me/cricketpredictongame" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.2929 6.30729L4.17512 12.9345C2.47692 13.7084 3.05399 15.2995 4.49096 15.8638L7.72725 16.9611L9.58082 21.2537C9.90151 22.2048 11.4096 22.2844 11.8214 21.1871L16.9039 11.1871C17.1658 10.6228 16.7541 9.9792 16.1771 10.1384L10.8007 12.2944C10.3395 12.4634 9.87838 12.0583 10.0465 11.5833L12.4664 6.46719C12.8193 5.59375 13.9931 5.59375 14.3459 6.46719L17.6408 13.4299C18.0132 14.4205 19.4501 14.0348 19.4501 12.9345V7.46719C19.4501 6.86259 19.0581 6.1289 18.4811 5.83406C17.9338 5.57947 16.9335 5.02341 16.9039 5.00391C16.5708 4.82435 16.1592 4.76407 15.8384 5.00391L3.30769 11.9792C2.2526 12.5139 2.39044 14.3133 3.51585 14.6989L17.4027 19.4397C18.6466 19.8942 19.9201 18.8048 19.6586 17.5058L17.3572 6.54237C17.2589 5.77849 16.1039 5.5139 15.868 6.24765L10.0465 19.0524C9.62403 20.0624 8.08185 19.9032 7.89274 18.8536L4.43947 5.40685C4.13844 4.51371 5.13873 3.70022 5.92279 4.11499L19.0196 11.1078C19.8037 11.5226 20.6669 10.5924 20.1177 9.90869L14.0541 2.25997C13.7333 1.8452 13.2031 1.64602 12.7123 1.64602H11.5869C10.6064 1.64602 10.31 2.93772 11.2313 3.33288L19.0196 7.30729C19.2618 7.42758 19.3997 7.66702 19.3997 7.92161V8.28662C19.3997 8.61138 19.1775 8.89101 18.8567 8.93165L6.21815 11.9495C5.73736 12.0299 5.73736 12.7229 6.21815 12.8033L18.8567 15.8212C19.1775 15.8618 19.3997 16.1414 19.3997 16.4662V16.8312C19.3997 17.0858 19.2618 17.3252 19.0196 17.4455L11.2313 21.4199C10.31 21.8151 10.6064 23.1068 11.5869 23.1068H12.7123C13.2031 23.1068 13.7333 22.9076 14.0541 22.4928L20.1177 14.8441C20.6669 14.1604 19.8037 13.2302 19.0196 13.645L5.92279 20.6378C5.13873 21.0526 4.13844 20.2391 4.43947 19.346L7.89274 5.89917C8.08185 4.84963 9.62403 4.69041 10.0465 5.7004L15.868 18.5051C16.1039 19.2389 17.2589 18.9743 17.3572 18.2104L19.6586 7.24696C19.9201 5.94797 18.6466 4.85859 17.4027 5.31304L3.51585 10.0539C2.39044 10.4395 2.2526 12.2389 3.30769 12.7736L15.8384 19.7489C16.1592 19.9887 16.5708 19.9284 16.9039 19.7489C16.9335 19.7294 17.9338 19.1733 18.4811 18.9187C19.0581 18.6239 19.4501 17.8902 19.4501 17.2856V11.8183C19.4501 10.718 18.0132 10.3323 17.6408 11.3229L14.3459 18.2856C13.9931 19.1591 12.8193 19.1591 12.4664 18.2856L10.0465 13.1695C9.87838 12.6945 10.3395 12.2894 10.8007 12.4585L16.1771 14.6144C16.7541 14.7736 17.1658 14.13 16.9039 13.5658L11.8214 3.56568C11.4096 2.46843 9.90151 2.548 9.58082 3.49911L7.72725 7.79172L4.49096 8.88899C3.05399 9.45329 2.47692 11.0444 4.17512 11.8183L19.2929 18.4455C20.0966 18.8603 21.0772 18.1174 20.8413 17.2339L19.3652 12.3702C19.3456 12.3098 19.3456 12.243 19.3652 12.1826L20.8413 7.31891C21.0772 6.43541 20.0966 5.69249 19.2929 6.10729V6.30729Z"/>
                </svg>
                Join our Telegram to get daily codes
              </a>
              <div className="text-xs text-gray-500 dark:text-gray-400 ml-5 mt-1">
                If link doesn't work, copy and paste: t.me/cricketpredictiongame
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      {message && (
        <div className={`p-3 rounded-lg mb-4 text-sm ${getMessageStyles()}`}>
          {message}
        </div>
      )}
      
      {showInput && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="dailyCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Enter Daily Code
            </label>
            <input
              type="text"
              id="dailyCode"
              value={dailyCode}
              onChange={(e) => setDailyCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter code here"
              disabled={loading || hasClaimed}
              required
            />
          </div>
          
          <button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || hasClaimed || !dailyCode.trim()}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              'Redeem Code'
            )}
          </button>
        </form>
      )}
      
      {hasClaimed && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
          Come back tomorrow for a new code!
        </div>
      )}
    </div>
  );
};

export default DailyCodeRedemption; 